import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { type ReactNode, useContext, useEffect, useState } from 'react'
import {
  type ApplicantHeadData,
  useApplicantsHead
} from '~/api/queries/applicant'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { CloseTrigger } from '~/components/Global/Interactions/CloseTrigger/CloseTrigger'
import {
  sideAction,
  sideActionDetailList,
  sideActionDetailListHeader,
  sideActionDetailListHeaderCount,
  sideActionDetailListItem,
  sideActionDetailListWrapper,
  sideActionHeader,
  sideActionPicto,
  sideActionSubtitle,
  sideActionSubtitleLink,
  sideActionTitle,
  sideActionTitleWrapper
} from '~/components/Scopes/Actions/AsideAction.css'
import { themeClass } from '~/styles/theme.css'
import { ApplicantModalContext, AsideActionContext } from '~/utils/contextes'
import { useCloseAction } from './hooks/useCloseAction'

type AsideActionProps = {
  children: ReactNode
  title: string
  icon: IconDefinition
  applicantIds: number[]
  onClose?: () => void
}

export const AsideAction = ({
  children,
  title,
  icon,
  applicantIds,
  onClose
}: AsideActionProps) => {
  const { data: applicantDataHead } = useApplicantsHead({
    applicantIds
  })
  const [applicantListShown, setApplicantListShown] = useState(false)
  const { position } = useContext(AsideActionContext)
  const closeAction = useCloseAction()
  return (
    <div className={clsx(sideAction, themeClass)}>
      <CloseTrigger
        onClose={() => {
          closeAction().then((confirm) => {
            if (confirm) {
              onClose?.()
            }
          })
        }}
      />
      <div className={sideActionHeader}>
        <div className={sideActionTitleWrapper}>
          <div className={sideActionPicto}>
            <FontAwesomeIcon icon={icon} />
          </div>
          <h3 className={sideActionTitle}>{title}</h3>
        </div>
        {position === 'sidebar' && (
          <div className={sideActionSubtitle}>
            {!applicantDataHead && applicantIds.length > 0 && (
              <Shimmer height={20} />
            )}
            {applicantDataHead &&
              applicantIds.length > 0 &&
              applicantDataHead.length < 3 &&
              `${applicantDataHead.map((applicant) => [applicant.firstname, applicant.lastname].join(' ')).join(', ')}`}
            {applicantDataHead && applicantDataHead.length >= 3 && (
              <>
                {applicantDataHead
                  .map((applicant) =>
                    [applicant.firstname, applicant.lastname].join(' ')
                  )
                  .slice(0, 2)
                  .join(', ')}{' '}
                et{' '}
                <span
                  className={sideActionSubtitleLink}
                  onClick={() => setApplicantListShown(true)}
                >
                  {applicantDataHead.length - 2} autre
                  {applicantDataHead.length - 2 > 1 ? 's' : ''}
                </span>
              </>
            )}
            <AnimatePresence>
              {applicantListShown && applicantDataHead && (
                <ApplicantList
                  applicants={applicantDataHead}
                  onClose={() => setApplicantListShown(false)}
                  key="applicants-list"
                />
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
      {children}
    </div>
  )
}

type ApplicantListProps = {
  applicants: ApplicantHeadData[]
  onClose: () => void
}

const ApplicantList = ({ applicants, onClose }: ApplicantListProps) => {
  const { setApplicantCollection, setApplicantCurrentId } = useContext(
    ApplicantModalContext
  )

  useEffect(() => {
    setApplicantCollection(applicants)
  }, [setApplicantCollection, applicants])

  return (
    <motion.div
      className={sideActionDetailList}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <CloseTrigger onClose={onClose} />
      <div className={sideActionDetailListHeader}>
        Sélection{' '}
        <span className={sideActionDetailListHeaderCount}>
          ({applicants?.length})
        </span>
      </div>
      <motion.div
        variants={{
          hidden: { opacity: 0 },
          visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
        }}
        initial="hidden"
        animate="visible"
        className={sideActionDetailListWrapper}
      >
        {applicants?.map((applicant) => (
          <motion.div
            variants={{
              initial: { opacity: 0, x: -20 },
              hidden: { opacity: 0, x: -20 },
              visible: { opacity: 1, x: 0 }
            }}
            key={applicant.id}
            className={sideActionDetailListItem}
            onClick={() => {
              setApplicantCurrentId(applicant.id)
            }}
          >
            {[applicant.firstname, applicant.lastname].join(' ')}
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  )
}
